import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { IOneTimePurchase, ISubscription, IUserGroup } from '@core/interfaces/user-group.interface';
import { SnackbarService } from '@core/services/snackbar.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { StartEndDatePickerComponent } from '@shared/components/start-end-date-picker/start-end-date-picker.component';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { addDays, endOfDay, isWithinInterval, startOfDay } from 'date-fns';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { IUserGroupUpdatePayload } from '../../admin-user-group.interface';
import { AdminUserGroupEditDialogStore } from './admin-user-group-edit-dialog.store';

export interface IAdminUserGroupEditDialogData {
  userGroup: IUserGroup;
}

@Component({
  imports: [
    DatePipe,
    DotsSpinnerComponent,
    IconComponent,
    MatButtonModule,
    MatErrorComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    NgxSkeletonLoaderModule,
    ReactiveFormsModule,
    SideDialogComponent,
    StartEndDatePickerComponent,
    TranslateModule,
    DatePickerComponent,
  ],
  providers: [AdminUserGroupEditDialogStore],
  selector: 'app-admin-user-group-edit-dialog',
  templateUrl: './admin-user-group-edit-dialog.component.html',
})
export class AdminUserGroupEditDialogComponent {
  public readonly form = new FormGroup({
    endDateRead: new FormControl<Date | null>(null),
    endDateWrite: new FormControl<Date | null>(null),
    id: new FormControl<string | null>(null),
    name: new FormControl<string>('', [Validators.required]),
    subscriptionId: new FormControl<string | null>(null, [Validators.required]),
  });

  public readonly purchaseForm = new FormGroup({
    end: new FormControl<Date | null>(null, [Validators.required]),
    extraConcurrentCampaigns: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    extraCreatorChannels: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    extraSeats: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    start: new FormControl<Date | null>(null, [Validators.required]),
  });

  public readonly userGroup = signal<IUserGroup | null>(null);
  public readonly localPurchases = signal<IOneTimePurchase[]>([]);
  public readonly selectedSubscriptionId = signal<string | null>(null);
  public readonly subscriptions = signal<ISubscription[]>([]);
  public readonly purchaseFormVisible = signal<boolean>(false);

  public readonly currentDay = computed(() => new Date());

  public readonly effectiveMaxValues = computed(() => {
    const purchases = this.localPurchases();
    const subscriptionId = this.selectedSubscriptionId();
    const subscriptions = this.subscriptions();
    const subscription = subscriptions.find((sub) => sub.id === subscriptionId);
    const currentDay = new Date();

    let seats = subscription?.maxSeats ?? 0;
    let channels = subscription?.maxCreatorChannels ?? 0;
    let campaigns = subscription?.maxConcurrentCampaigns ?? 0;

    purchases.forEach((purchase) => {
      if (!isWithinInterval(currentDay, { end: purchase.end, start: purchase.start })) {
        return;
      }

      if (seats >= 0) {
        seats += purchase.extraSeats;
      }

      if (channels >= 0) {
        channels += purchase.extraCreatorChannels;
      }

      if (campaigns >= 0) {
        campaigns += purchase.extraConcurrentCampaigns;
      }
    });

    return {
      campaigns: campaigns < 0 ? null : campaigns,
      channels: channels < 0 ? null : channels,
      seats: seats < 0 ? null : seats,
    };
  });

  public readonly store = inject(AdminUserGroupEditDialogStore);
  public readonly data = inject<IAdminUserGroupEditDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<AdminUserGroupEditDialogComponent>);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly dateUtils = inject(DateUtilitiesService);

  constructor() {
    this.initialize();
    this.store.fetchSubscriptions();
    this.setupEffects();
  }

  public onCloseClick(): void {
    this.dialogRef.close();
  }

  public onSaveClick(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      return;
    }

    const { controls, value } = this.form;

    const payload: IUserGroupUpdatePayload = {
      endDateRead: controls.endDateRead.value ? this.dateUtils.formatToApiDateTime(controls.endDateRead.value) : null,
      endDateWrite: controls.endDateWrite.value
        ? this.dateUtils.formatToApiDateTime(controls.endDateWrite.value)
        : null,
      name: value.name!,
      oneTimePurchases: this.localPurchases(),
      subscriptionId: value.subscriptionId || null,
    };

    this.store.updateUserGroup({
      id: this.data.userGroup.id,
      payload,
    });
  }

  public onAddPurchaseClick(): void {
    this.purchaseForm.updateValueAndValidity();

    if (this.purchaseForm.invalid || !this.purchaseForm.value.start || !this.purchaseForm.value.end) {
      this.purchaseForm.markAllAsTouched();

      return;
    }

    const start = this.dateUtils.formatToApiDateTime(new Date(this.purchaseForm.value.start));
    const end = this.dateUtils.formatToApiDateTime(new Date(this.purchaseForm.value.end));

    if (!start || !end) {
      return;
    }

    const purchase: IOneTimePurchase = {
      end,
      endDisplay: new Date(this.purchaseForm.value.end),
      extraConcurrentCampaigns: this.purchaseForm.value.extraConcurrentCampaigns!,
      extraCreatorChannels: this.purchaseForm.value.extraCreatorChannels!,
      extraSeats: this.purchaseForm.value.extraSeats!,
      start,
      startDisplay: new Date(this.purchaseForm.value.start),
    };

    this.localPurchases.update((purchases) => [...purchases, purchase]);
    this.purchaseForm.reset();
    this.purchaseFormVisible.set(false);
  }

  public onAddCampaignSprintClick(): void {
    const start = startOfDay(new Date());
    const end = endOfDay(addDays(new Date(), 45));

    this.purchaseForm.patchValue({
      end,
      extraConcurrentCampaigns: 10,
      extraCreatorChannels: 10,
      extraSeats: 2,
      start,
    });

    this.purchaseFormVisible.set(true);
  }

  public onDeletePurchaseClick(index: number): void {
    this.localPurchases.update((purchases) => purchases.filter((_, i) => i !== index));
  }

  public onSubscriptionChange(subscriptionId: string): void {
    this.selectedSubscriptionId.set(subscriptionId || null);
  }

  public onCancelPurchaseClick(): void {
    this.purchaseFormVisible.set(false);
    this.purchaseForm.reset();
  }

  private initialize(): void {
    this.userGroup.set(this.data.userGroup);
    this.localPurchases.set(
      this.data.userGroup.oneTimePurchases
        ? [...this.data.userGroup.oneTimePurchases].map((purchase) => ({
            ...purchase,
            endDisplay: this.dateUtils.parseDateTimeFromApi(purchase.end),
            startDisplay: this.dateUtils.parseDateTimeFromApi(purchase.start),
          }))
        : [],
    );
    this.selectedSubscriptionId.set(this.data.userGroup.subscriptionId || null);

    this.form.controls.id.disable();
    this.form.patchValue({
      endDateRead: this.data.userGroup.endDateRead
        ? this.dateUtils.parseDateTimeFromApi(this.data.userGroup.endDateRead)
        : null,
      endDateWrite: this.data.userGroup.endDateWrite
        ? this.dateUtils.parseDateTimeFromApi(this.data.userGroup.endDateWrite)
        : null,
      id: this.data.userGroup.id,
      name: this.data.userGroup.name,
      subscriptionId: this.data.userGroup.subscriptionId || null,
    });
    this.purchaseForm.reset();
  }

  private setupEffects(): void {
    effect(() => {
      const subscriptions = this.store.subscriptions$();

      if (subscriptions) {
        this.subscriptions.set(subscriptions);
      }
    });

    effect(() => {
      const { subscriptionId } = this.form.value;

      if (subscriptionId !== undefined) {
        this.selectedSubscriptionId.set(subscriptionId || null);
      }
    });

    effect(() => {
      if (!this.store.loading$() && this.store.updatedUserGroup$()) {
        this.userGroup.set(this.store.updatedUserGroup$()!);
        this.snackbarService.openSuccess(this.translate.instant('shared.changesSavedSuccessfully'));

        this.dialogRef.close(true);
      }
    });
  }
}
